import {
	ChangeDetectionStrategy,
	Component,
	DestroyRef,
	inject,
	input,
	OnChanges,
	OnInit,
	output,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { IWorkOrderRoutine } from '@app/domain/cmms-types/workorder-types';
import { FormFieldLabelComponent } from '@app/shared/ui-components/form-field-label/form-field-label.component';
import { NgxTolgeeModule } from '@tolgee/ngx';
import { EntityDeadlineComponent } from './entity-deadline/entity-deadline.component';
import {
	CmmsScheduleMonthChoices,
	CmmsSchedulePeriodsType,
	DaysOfWeekType,
} from '@app/pages/cmms/interfaces/scheduled-value.interface';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime } from 'rxjs';

@Component({
	selector: 'entity-schedule',
	imports: [
		NgxTolgeeModule,
		MatSlideToggleModule,
		MatRadioModule,
		FormFieldLabelComponent,
		MatFormFieldModule,
		MatInputModule,
		MatIconModule,
		ReactiveFormsModule,
		MatSelectModule,
		EntityDeadlineComponent,
	],
	templateUrl: './entity-schedule.component.html',
	styleUrl: './entity-schedule.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EntityScheduleComponent implements OnInit, OnChanges {
	private readonly destroyRef = inject(DestroyRef);
	routineTemplate = input<boolean>(false);
	selectedSchedule = input<IWorkOrderRoutine>();
	deadline = input<Date | null>(null);
	canEditTemplate = input<boolean>(true);
	scheduleChanged = output<IWorkOrderRoutine>();
	deadlineChanged = output<Date>();

	interval = new FormControl<number>(1);
	period = new FormControl<CmmsSchedulePeriodsType>('DAY');
	excludeWeekends = new FormControl<boolean>(true);
	selectedDays = new FormControl<DaysOfWeekType>(null);
	monthSetup = new FormControl<CmmsScheduleMonthChoices>(null);

	scheduleForm: FormGroup = new FormGroup({
		interval: this.interval,
		period: this.period,
		excludeWeekends: this.excludeWeekends,
		selectedDays: this.selectedDays,
		monthSetup: this.monthSetup,
	});

	periods: { [key: string]: string }[] = [
		{
			value: 'DAY',
			name: 'day',
		},
		{
			value: 'WEEK',
			name: 'week',
		},
		{
			value: 'MONTH',
			name: 'month',
		},
		{
			value: 'YEAR',
			name: 'year',
		},
	];

	selectedDaysValues: string[] = [
		'monday',
		'tuesday',
		'wednesday',
		'thursday',
		'friday',
		'saturday',
		'sunday',
	];
	monthSetupValues: { value: string; name: string }[] = [
		{ value: 'FIRST_DAY', name: 'periods_common_first_day_of_month' },
		{ value: 'LAST_DAY', name: 'periods_common_last_day_of_month' },
	];

	ngOnInit() {
		this.scheduleForm.valueChanges
			.pipe(takeUntilDestroyed(this.destroyRef), debounceTime(500))
			.subscribe(formValue => {
				formValue.selectedDays = [formValue.selectedDays];
				if (JSON.stringify(formValue) !== JSON.stringify(this.selectedSchedule())) {
					this.scheduleChanged.emit({
						interval: formValue.interval,
						period: formValue.period,
						excludeWeekends: formValue.excludeWeekends,
						selectedDays:
							formValue.selectedDays.length > 0 ? formValue.selectedDays : ['monday'],
						monthSetup: formValue.monthSetup ? formValue.monthSetup : 'FIRST_DAY',
					});
					if (formValue.period === 'WEEK' && !formValue.selectedDays) {
						this.selectedDays.setValue('monday');
					}
					if (formValue.period === 'MONTH' && !formValue.monthSetup) {
						this.monthSetup.setValue('FIRST_DAY');
					}
				}
			});
	}

	changedWOType(value: string) {
		if (value === 'routine' && !this.selectedSchedule()) {
			this.interval.setValue(1);
			this.period.setValue('DAY');
			this.excludeWeekends.setValue(true);
		} else if (value === 'work_order' && this.selectedSchedule()) {
			return this.scheduleChanged.emit(null);
		}
	}

	decreaseInterval() {
		if (!this.canEditTemplate()) {
			return;
		}
		if (this.interval.value > 1) {
			this.interval.setValue(this.interval.value - 1);
			return;
		}
	}

	increaseInterval() {
		if (this.canEditTemplate()) {
			this.interval.setValue((this.interval.value || 0) + 1);
		}
	}

	ngOnChanges() {
		this.interval.setValue(this.selectedSchedule()?.interval);
		this.period.setValue(this.selectedSchedule()?.period);
		this.excludeWeekends.setValue(this.selectedSchedule()?.excludeWeekends);
		this.selectedDays.setValue(
			(this.selectedSchedule()?.selectedDays?.[0] as DaysOfWeekType) || 'monday'
		);
		this.monthSetup.setValue(this.selectedSchedule()?.monthSetup || 'FIRST_DAY');
		if (!this.canEditTemplate()) {
			this.scheduleForm.disable();
			return;
		}
		this.scheduleForm.enable();
	}
}
